import type {
  CanvasSnapshotBlock,
  CanvasSnapshotRevision,
} from './canvasSnapshot';
import { REVISION_ABSENTE } from './fencesDeMutation';

/** A provider revision that Hector created and re-read on this exact mounted document. */
export interface AgentTrackedInsertion {
  nodeId: string;
  changeId: string;
}

export type AgentTrackedInsertionWithdrawalPlan =
  | { kind: 'none' }
  | { kind: 'reject'; entries: readonly AgentTrackedInsertion[] }
  | {
    kind: 'blocked';
    code:
      | 'AGENT_TRACKED_INSERTION_PROVENANCE_STALE'
      | 'AGENT_TRACKED_INSERTION_RANGE_MIXED'
      | 'AGENT_TRACKED_INSERTION_HAS_OTHER_REVISION'
      | 'AGENT_TRACKED_INSERTION_HAS_UNANCHORED_REVISION';
    message: string;
  };

/**
 * Qualifies the provider representation of a paragraph created by
 * `create.paragraph`; it does not establish who created the revision.
 *
 * SuperDoc 2.15.1 returns `insertion / text-insertion` for this native
 * paragraph operation in Chromium (measured 2026-09-18), while an earlier
 * qualified runtime returned `structural / paragraph-insertion`. Both remain
 * narrowly accepted for compatibility. Ownership is established separately by
 * the ledger's exact nodeId/changeId/revision causal chain.
 */
export function isOpenParagraphInsertionRevision(
  revision: CanvasSnapshotRevision,
  nodeId: string,
): boolean {
  if (revision.state !== 'open' || revision.blockId !== nodeId) return false;
  return (revision.type === 'structural' && revision.subtype === 'paragraph-insertion')
    || (revision.type === 'insertion' && revision.subtype === 'text-insertion');
}

/**
 * Ephemeral causal provenance for a paragraph insertion, not Word authorship.
 *
 * SuperDoc v2 signs agent edits with the connected lawyer identity, so
 * `trackChanges.list().author` cannot distinguish an agent proposal from a
 * human edit. This ledger only remembers the narrower fact that the current
 * browser instance created a native paragraph and immediately re-read its
 * exact qualified paragraph-insertion revision. It is intentionally neither persisted nor shared
 * across mounts: after an unobserved revision, the safe answer is no automatic
 * review decision.
 */
export interface AgentTrackedInsertionLedger {
  record(input: {
    preRevision: string;
    postRevision: string;
    entries: readonly AgentTrackedInsertion[];
  }): void;
  planWithdrawal(input: {
    revision: string;
    blocks: readonly CanvasSnapshotBlock[];
    trackedChanges: readonly CanvasSnapshotRevision[];
  }): AgentTrackedInsertionWithdrawalPlan;
  /** Forget only revisions that a post-decision snapshot proved settled. */
  forgetSettledRevisions(changeIds: readonly string[]): void;
  remove(nodeIds: readonly string[]): void;
  advanceAfterAgentAction(input: { preRevision: string; postRevision: string }): void;
  invalidate(): void;
}

class MemoireDesInsertionsSuiviesAgent implements AgentTrackedInsertionLedger {
  private trustedRevision: string | null = null;
  private readonly entries = new Map<string, string>();

  record(input: {
    preRevision: string;
    postRevision: string;
    entries: readonly AgentTrackedInsertion[];
  }): void {
    if (input.entries.length === 0) return;
    // `unknown` is a display sentinel, never an optimistic document revision.
    // Recording it would let two missing reads compare equal and manufacture
    // automatic review authority after a state we cannot name.
    if (input.preRevision === REVISION_ABSENTE || input.postRevision === REVISION_ABSENTE) {
      this.invalidate();
      return;
    }
    // A user edit (or an action we did not observe) breaks the causal chain.
    // The new verified insertion remains safe to record, but old associations
    // cannot be carried across that gap.
    if (this.trustedRevision !== null && this.trustedRevision !== input.preRevision) {
      this.invalidate();
    }
    for (const entry of input.entries) this.entries.set(entry.nodeId, entry.changeId);
    this.trustedRevision = input.postRevision;
  }

  planWithdrawal(input: {
    revision: string;
    blocks: readonly CanvasSnapshotBlock[];
    trackedChanges: readonly CanvasSnapshotRevision[];
  }): AgentTrackedInsertionWithdrawalPlan {
    const entriesInRange = input.blocks.flatMap((block) => {
      const changeId = this.entries.get(block.nodeId);
      return changeId === undefined ? [] : [{ nodeId: block.nodeId, changeId }];
    });
    if (entriesInRange.length === 0) return { kind: 'none' };

    if (input.revision === REVISION_ABSENTE) {
      this.invalidate();
      return {
        kind: 'blocked',
        code: 'AGENT_TRACKED_INSERTION_PROVENANCE_STALE',
        message: 'The current document revision is unavailable, so the recorded agent insertion cannot be tied to this document state. Nothing was deleted or rejected automatically; inspect the document before choosing a review action.',
      };
    }

    if (this.trustedRevision !== input.revision) {
      this.invalidate();
      return {
        kind: 'blocked',
        code: 'AGENT_TRACKED_INSERTION_PROVENANCE_STALE',
        message: 'The recorded agent insertion is no longer on the same observed document revision. Nothing was deleted or rejected automatically; inspect the current document before choosing a review action.',
      };
    }

    if (entriesInRange.length !== input.blocks.length) {
      return {
        kind: 'blocked',
        code: 'AGENT_TRACKED_INSERTION_RANGE_MIXED',
        message: 'The requested range mixes a verified agent paragraph insertion with ordinary blocks. Nothing was modified: automatically rejecting only part of the range could create a partial deletion.',
      };
    }

    const causalChangeIds = new Set(entriesInRange.map((entry) => entry.changeId));
    const foreignUnanchoredRevisions = input.trackedChanges.filter((revision) => (
      !causalChangeIds.has(revision.id) && revision.blockId === undefined
    ));
    if (foreignUnanchoredRevisions.length > 0) {
      this.invalidate();
      return {
        kind: 'blocked',
        code: 'AGENT_TRACKED_INSERTION_HAS_UNANCHORED_REVISION',
        message: `The document has ${foreignUnanchoredRevisions.length} other active tracked revision(s) without a usable blockId. Nothing was rejected automatically because their relation to the agent-created paragraph cannot be proven safe.`,
      };
    }

    for (const entry of entriesInRange) {
      const foreignRevisionsOnBlock = input.trackedChanges.filter((revision) => (
        revision.blockId === entry.nodeId
        && revision.id !== entry.changeId
      ));
      if (foreignRevisionsOnBlock.length > 0) {
        this.invalidate();
        return {
          kind: 'blocked',
          code: 'AGENT_TRACKED_INSERTION_HAS_OTHER_REVISION',
          message: `Block nodeId=${entry.nodeId} now carries ${foreignRevisionsOnBlock.length} other tracked revision(s) in addition to the recorded agent insertion changeId=${entry.changeId}. Nothing was rejected automatically because that could remove later lawyer work on the same paragraph.`,
        };
      }
      const revisions = input.trackedChanges.filter((revision) => revision.id === entry.changeId);
      const revision = revisions[0];
      if (revisions.length !== 1 || revision === undefined || !isOpenParagraphInsertionRevision(revision, entry.nodeId)) {
        this.invalidate();
        return {
          kind: 'blocked',
          code: 'AGENT_TRACKED_INSERTION_PROVENANCE_STALE',
          message: `The recorded agent insertion for block nodeId=${entry.nodeId} no longer has its exact open paragraph-insertion revision changeId=${entry.changeId}. Nothing was modified automatically; inspect the current document before choosing a review action.`,
        };
      }
    }

    return { kind: 'reject', entries: entriesInRange };
  }

  forgetSettledRevisions(changeIds: readonly string[]): void {
    if (changeIds.length === 0) return;
    const settled = new Set(changeIds);
    for (const [nodeId, changeId] of this.entries) {
      if (settled.has(changeId)) this.entries.delete(nodeId);
    }
    if (this.entries.size === 0) this.trustedRevision = null;
  }

  remove(nodeIds: readonly string[]): void {
    for (const nodeId of nodeIds) this.entries.delete(nodeId);
    if (this.entries.size === 0) this.trustedRevision = null;
  }

  advanceAfterAgentAction(input: { preRevision: string; postRevision: string }): void {
    if (this.entries.size === 0) return;
    if (input.preRevision === REVISION_ABSENTE || input.postRevision === REVISION_ABSENTE) {
      this.invalidate();
      return;
    }
    // `record()` may already have advanced the same ledger during a successful
    // insert_paragraphs action. Do not mistake that expected post-revision for
    // an unobserved edit.
    if (this.trustedRevision === input.postRevision) return;
    if (this.trustedRevision !== input.preRevision) {
      this.invalidate();
      return;
    }
    this.trustedRevision = input.postRevision;
  }

  invalidate(): void {
    this.entries.clear();
    this.trustedRevision = null;
  }
}

/** A ledger lives only as long as the provider instance and its mount epoch. */
const ledgersByInstance = new WeakMap<object, Map<string, AgentTrackedInsertionLedger>>();

function mountKey(mount: { conversationId: string; mountEpoch: number }): string {
  return `${mount.conversationId}\u0000${String(mount.mountEpoch)}`;
}

export function ledgerForAgentTrackedInsertions(
  instance: object,
  mount: { conversationId: string; mountEpoch: number },
): AgentTrackedInsertionLedger {
  let ledgersByMount = ledgersByInstance.get(instance);
  if (ledgersByMount === undefined) {
    ledgersByMount = new Map();
    ledgersByInstance.set(instance, ledgersByMount);
  }
  const key = mountKey(mount);
  let ledger = ledgersByMount.get(key);
  if (ledger === undefined) {
    ledger = new MemoireDesInsertionsSuiviesAgent();
    ledgersByMount.set(key, ledger);
  }
  return ledger;
}

/** Exposed for focused tests; production callers obtain the instance-bound ledger above. */
export function createAgentTrackedInsertionLedgerForTests(): AgentTrackedInsertionLedger {
  return new MemoireDesInsertionsSuiviesAgent();
}
